import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function AuthPage() {
  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ageVerified, setAgeVerified] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }

    if (mode === 'signup') {
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters.');
        return;
      }
      if (!ageVerified) {
        setError('You must confirm you are 21 or older to use AI Sports Edge.');
        return;
      }
    }

    setSubmitting(true);

    if (mode === 'signin') {
      const { error: err } = await signIn(email, password);
      if (err) {
        setError(err.message);
      }
      // Redirect happens via useEffect when user updates
    } else {
      const { data, error: err } = await signUp(email, password);
      if (err) {
        setError(err.message);
      } else if (data?.user?.identities?.length === 0) {
        setError('An account with this email already exists.');
      } else {
        // Fire-and-forget: store signup metadata on the auto-created profile row
        if (data?.user?.id) {
          supabase.from('user_profiles').update({
            age_verified_at: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            referral_source: new URLSearchParams(window.location.search).get('ref') || null,
          }).eq('id', data.user.id).then(() => {});
        }
        setSuccess('Account created! Check your email for a confirmation link.');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setAgeVerified(false);
      }
    }

    setSubmitting(false);
  }

  function switchMode() {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setError(null);
    setSuccess(null);
    setAgeVerified(false);
  }

  const inputCls = "w-full px-3 py-2 rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#0f1219] text-[14px] text-[#111827] dark:text-[#e2e8f0] placeholder-[#9CA3AF] focus:outline-none focus:border-[#005BB3] dark:focus:border-[#3b82f6] transition-colors duration-150";
  const labelCls = "text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8] block mb-1";

  return (
    <div className="min-h-[calc(100vh-48px)] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-[400px]">
        {/* Card */}
        <div className="rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] shadow-sm px-6 py-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#005BB3] flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-[14px] font-bold">AI</span>
            </div>
            <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
              AI Sports Edge
            </h1>
            <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mt-1" style={{ fontFamily: 'var(--font-sans)' }}>
              {mode === 'signin' ? 'Sign in to your account' : 'Create a new account'}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-4">
              <p className="text-[#DC2626] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>{error}</p>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="rounded bg-[rgba(34,197,94,0.06)] dark:bg-[rgba(34,197,94,0.08)] border border-[#bbf7d0] dark:border-[#14532d] px-3 py-2 mb-4">
              <p className="text-[#029456] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="space-y-3">
              <div>
                <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" autoComplete="email"
                  className={inputCls} style={{ fontFamily: 'var(--font-sans)' }}
                />
              </div>
              <div>
                <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password" autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  className={inputCls} style={{ fontFamily: 'var(--font-sans)' }}
                />
              </div>
              {mode === 'signup' && (
                <>
                  <div>
                    <label className={labelCls} style={{ fontFamily: 'var(--font-sans)' }}>Confirm Password</label>
                    <input
                      type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password" autoComplete="new-password"
                      className={inputCls} style={{ fontFamily: 'var(--font-sans)' }}
                    />
                  </div>
                  <div className="flex items-start gap-2.5 pt-1">
                    <input
                      type="checkbox" id="age-verify"
                      checked={ageVerified} onChange={(e) => setAgeVerified(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#005BB3]"
                    />
                    <label htmlFor="age-verify" className="text-[14px] text-[#6B7280] dark:text-[#8892a8] leading-snug cursor-pointer select-none" style={{ fontFamily: 'var(--font-sans)' }}>
                      I confirm that I am 21 years of age or older
                    </label>
                  </div>
                </>
              )}
            </div>

            <button type="submit" disabled={submitting}
              className="w-full mt-5 px-4 py-2.5 rounded-md bg-[#005BB3] hover:bg-[#004494] disabled:opacity-40 text-white text-[14px] font-semibold transition-colors duration-150"
              style={{ fontFamily: 'var(--font-sans)' }}>
              {submitting ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          {/* Toggle link */}
          <p className="text-center text-[14px] text-[#6B7280] dark:text-[#8892a8] mt-4" style={{ fontFamily: 'var(--font-sans)' }}>
            {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
            <button onClick={switchMode} className="text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] font-medium transition-colors duration-150">
              {mode === 'signin' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
